import { useCallback, useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import type {
  BasketItem,
  DictionaryCategory,
  DictionaryIngredient,
} from '@entities/calculator'
import {
  useDeleteBasketItemMutation,
  useWriteBasketItemMutation,
} from '@entities/calculator'
import { CALC_EVENTS, useTrackAction } from './analytics'
import IngredientPickerSheet from './IngredientPickerSheet'
import { removeErrorText, writeErrorText } from './writeErrorText'
import ProductEditorModal from './ProductEditorModal'
import ProductRow from './ProductRow'

interface ProductsTabProps {
  items: readonly BasketItem[]
  ingredients: readonly DictionaryIngredient[]
  /** Категории в порядке показа — по ним группируется корзина. */
  categories: readonly DictionaryCategory[]
  isLoading: boolean
}

interface EditingState {
  ingredient: DictionaryIngredient
  item?: BasketItem
}

/**
 * «Мои продукты» — цены закупки. Из них считается себестоимость рецептов:
 * без цены продукт в расчёт не попадает.
 */
const ProductsTab = ({
  items,
  ingredients,
  categories,
  isLoading,
}: ProductsTabProps) => {
  const [isPickerOpen, setPickerOpen] = useState(false)
  const [editing, setEditing] = useState<EditingState | null>(null)
  // Продукт записан — шторке пора закрываться, но не рывком: она сама доиграет
  // анимацию и уже тогда снимется с экрана.
  const [isSaved, setSaved] = useState(false)
  // Удаляемые держим списком: у мутации состояние одно, последнего вызова, и
  // при двух удалениях подряд индикатор повис бы на чужой строке.
  const [removingIds, setRemovingIds] = useState<readonly number[]>([])

  const write = useWriteBasketItemMutation()
  const remove = useDeleteBasketItemMutation()
  const track = useTrackAction()

  const ingredientById = useMemo(
    () => new Map(ingredients.map(item => [item.ingredient_id, item])),
    [ingredients]
  )

  // Порядок справочника задаёт админка — держим его и в корзине, чтобы
  // продукты не прыгали между экранами.
  const order = useMemo(
    () =>
      new Map(ingredients.map((item, index) => [item.ingredient_id, index])),
    [ingredients]
  )
  const sorted = useMemo(
    () =>
      [...items].sort(
        (a, b) =>
          (order.get(a.ingredient_id) ?? Number.MAX_SAFE_INTEGER) -
          (order.get(b.ingredient_id) ?? Number.MAX_SAFE_INTEGER)
      ),
    [items, order]
  )

  const usedIds = useMemo(
    () => new Set(items.map(item => item.ingredient_id)),
    [items]
  )

  // Корзину раскладываем по категориям справочника, порядок категорий — с
  // бэка. Заголовки показываем только когда групп больше одной.
  const groups = useMemo(() => {
    const byCategory = new Map<number, BasketItem[]>()
    sorted.forEach(item => {
      const categoryId =
        ingredientById.get(item.ingredient_id)?.category_id ?? 0
      const bucket = byCategory.get(categoryId)
      if (bucket) bucket.push(item)
      else byCategory.set(categoryId, [item])
    })
    return categories
      .map(category => ({
        category,
        items: byCategory.get(category.category_id) ?? [],
      }))
      .filter(group => group.items.length > 0)
  }, [sorted, categories, ingredientById])

  const closePicker = useCallback(() => setPickerOpen(false), [])

  const closeEditor = useCallback(() => {
    setEditing(null)
    setSaved(false)
    write.reset()
  }, [write])

  const handleSubmit = (amount: string, price: string) => {
    if (!editing) return
    const isEdit = Boolean(editing.item)
    write.mutate(
      {
        ingredient_id: editing.ingredient.ingredient_id,
        amount,
        price,
        exists: isEdit,
      },
      {
        onSuccess: () => {
          // Правку цены в минимум не берём: важно, сколько продуктов человек
          // завёл, а не сколько раз потом уточнял.
          if (!isEdit) {
            track(
              CALC_EVENTS.saveProduct,
              String(editing.ingredient.ingredient_id)
            )
          }
          setSaved(true)
        },
      }
    )
  }

  const handleRemove = (ingredientId: number) => {
    setRemovingIds(current => [...current, ingredientId])
    remove.mutate(ingredientId, {
      onSettled: () =>
        setRemovingIds(current => current.filter(id => id !== ingredientId)),
    })
  }

  return (
    <div className="cost-tab">
      <p className="cost-tab__lead">
        Укажите, каким объёмом и за сколько покупаете продукты. Цену за грамм
        посчитаем сами — она нужна для себестоимости рецептов.
      </p>

      {isLoading && <p className="cost-note">Загружаем ваши продукты…</p>}

      {!isLoading && sorted.length === 0 && (
        <p className="cost-note">
          Пока пусто. Добавьте продукты, которые покупаете, — хотя бы те, что
          встречаются в рецептах чаще всего.
        </p>
      )}

      {groups.map(group => (
        <div key={group.category.category_id} className="cost-products">
          {groups.length > 1 && (
            <p className="cost-products__group">{group.category.name}</p>
          )}
          {group.items.map(item => {
            const ingredient = ingredientById.get(item.ingredient_id)
            return (
              <ProductRow
                key={item.ingredient_id}
                item={item}
                ingredient={ingredient}
                onEdit={() => ingredient && setEditing({ ingredient, item })}
                onRemove={() => handleRemove(item.ingredient_id)}
                isRemoving={removingIds.includes(item.ingredient_id)}
              />
            )
          })}
        </div>
      ))}

      {remove.isError && (
        <p className="cost-note warning">
          {removeErrorText(remove.error ?? null)}
        </p>
      )}

      <button
        type="button"
        className="cost-button wide"
        onClick={() => setPickerOpen(true)}
        disabled={ingredients.length === 0}
      >
        <Plus size={18} aria-hidden />
        Добавить продукт
      </button>

      {isPickerOpen && (
        <IngredientPickerSheet
          ingredients={ingredients}
          usedIds={usedIds}
          onClose={closePicker}
          onPick={ingredient => {
            setPickerOpen(false)
            setEditing({
              ingredient,
              item: items.find(
                one => one.ingredient_id === ingredient.ingredient_id
              ),
            })
          }}
        />
      )}

      {editing && (
        <ProductEditorModal
          // Ключ по продукту: новая позиция открывается со своей анимацией, а
          // не наследует состояние предыдущей.
          key={editing.ingredient.ingredient_id}
          ingredient={editing.ingredient}
          item={editing.item}
          isSaving={write.isLoading}
          error={writeErrorText(write.error ?? null)}
          closeRequested={isSaved}
          onSubmit={handleSubmit}
          onClose={closeEditor}
        />
      )}
    </div>
  )
}

export default ProductsTab
